import React, { useMemo } from 'react';
import {
  SelfClassification,
  SelfDeclarationDto,
  SelfDeclarationUpdateDto,
  SivaCompanyDetailDto,
  SivaContractDetailDto,
  SivaContractUpdateDto,
  SivaPhase,
} from 'src/api/v2';
import { bransjeOptions } from '../../constants';
import {
  Checkbox,
  CheckboxOptionProps,
  DateInput,
  RadioButton,
  SelectOptionProps,
  TextArea,
} from '@innovationnorway/component-library';
import ValueField from 'src/components/ValueField/ValueField';
import useAdvisorOptions from '../../hooks/use-advisor-options';
import { useCalculateSivaKTUDates } from 'src/hooks/use-siva-ktu-date-calculation';
import { getMonthAndYearString, toIsoDateString } from 'src/utils/date';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import useCompanyContract from '../../company-details/hooks/use-company-contract';
import { requiredI18n } from 'src/utils/validation';
import { SelectWrapper } from 'src/components/Form/SelectWrapper';
import useToggle from 'src/hooks/use-toggle';
import ConnectCompany from '../../company-details/components/ConnectCompany';
import { Autocomplete } from 'src/components/Autocomplete/Autocomplete';
import { formatDateToNOString } from 'src/utils/FormatValue';
import { Alert, Button, Heading, Icon } from '@innovationnorway/cl-react';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';
import { UseMutationResult } from '@tanstack/react-query';
type Props = {
  contract: SivaContractDetailDto;
  selectedPhase: SivaPhase;
  company?: SivaCompanyDetailDto;
  onCancel: () => void;
  selfDeclarationDto?: SelfDeclarationDto;
  declarationUpsertDto: UseMutationResult<SelfDeclarationDto[], unknown, SelfDeclarationUpdateDto, unknown>;
};

const IdeaChangePhaseForm: React.FC<Props> = ({
  contract,
  selectedPhase,
  company,
  onCancel,
  selfDeclarationDto,
  declarationUpsertDto,
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tSiva } = useTranslation('siva');
  const { t: tError } = useTranslation('error');

  const [selectedAdmissionDate, setSelectedAdmissionDate] = React.useState<Date | null>(null);
  const [companyError, setCompanyError] = React.useState(false);
  const [orgNumber, setOrgNumber] = React.useState<string>('');
  const [companyName, setCompanyName] = React.useState<string>('');
  const [selectedSelfDeclaration, setSelectedSelfDeclaration] = React.useState<Set<SelfClassification>>(
    new Set(selfDeclarationDto?.selfClassification || []),
  );
  const [delivered, setDelivered] = React.useState<'no' | 'yes'>(
    selfDeclarationDto?.selfDeclarationDelivered ? 'yes' : 'no',
  );

  const { advisorOptions } = useAdvisorOptions();
  const { currentPhase, updateSivaContractMutation } = useCompanyContract(contract.numericId);

  const [updateErrorType, setUpdateErrorType] = React.useState<'phase' | 'selfDeclaration' | undefined>(
    undefined,
  );
  const { activate, deactivate, isActive } = useToggle();

  const { register, handleSubmit, control } = useForm<SivaContractUpdateDto>({
    defaultValues: {
      ...contract,
      newPhase: {
        newPhase: undefined,
      },
      advisorId: contract?.advisorId,
    },
  });

  const onSave = handleSubmit(async (formData) => {
    try {
      await updateSivaContractMutation.mutateAsync({
        ...formData,
        newPhase: {
          newPhase: selectedPhase,
          fromDate: selectedAdmissionDate!,
        },
        organizationNumber: company ? company.organizationNumber : orgNumber || '',
        externalIndustry: formData.externalIndustry,
        advisorId: formData.advisorId,
        notes: formData.notes,
      });
    } catch {
      setUpdateErrorType('phase');
      activate();
      return;
    }

    if (selectedSelfDeclaration) {
      try {
        await declarationUpsertDto.mutateAsync({
          sivaContractId: contract.id,
          deliveredDate: selectedAdmissionDate!,
          financialYear: new Date().getFullYear(),
          organizationNumber: company?.organizationNumber || orgNumber,
          selfDeclarationDelivered: delivered === 'yes',
          selfClassification: selectedSelfDeclaration ? Array.from(selectedSelfDeclaration) : [],
        });
      } catch {
        setUpdateErrorType('selfDeclaration');
        activate();
        return;
      }
    }

    setUpdateErrorType(undefined);
    activate();
  });

  const options: CheckboxOptionProps[] = useMemo(() => {
    return [
      {
        text: tSiva('reporting.selfdeclaration.Partner'),
        value: SelfClassification.Partner,
        disabled: selectedSelfDeclaration.has(SelfClassification.Independent),
        selected: selectedSelfDeclaration.has(SelfClassification.Partner),
      },
      {
        text: tSiva('reporting.selfdeclaration.Associated'),
        value: SelfClassification.Associated,
        disabled: selectedSelfDeclaration.has(SelfClassification.Independent),
        selected: selectedSelfDeclaration.has(SelfClassification.Associated),
      },
      {
        text: tSiva('reporting.selfdeclaration.Independent'),
        value: SelfClassification.Independent,
        disabled:
          selectedSelfDeclaration.has(SelfClassification.Partner) ||
          selectedSelfDeclaration.has(SelfClassification.Associated),
        selected: selectedSelfDeclaration.has(SelfClassification.Independent) || false,
      },
    ];
  }, [selectedSelfDeclaration, tSiva]);

  const [startupDate, midDate] = useCalculateSivaKTUDates(selectedAdmissionDate);

  const renderFormSendingDate = (date: Date | undefined) => {
    if (!date) {
      return 'Opptaksdato ikke satt';
    }

    return getMonthAndYearString(date);
  };

  const onChangeDeclaration = (e) => {
    if (selectedSelfDeclaration.has(e.currentTarget.value)) {
      const newSet = new Set(selectedSelfDeclaration);
      newSet.delete(e.currentTarget.value);
      return setSelectedSelfDeclaration(newSet);
    }
    setSelectedSelfDeclaration((prev) => new Set(prev).add(e.currentTarget.value));
  };

  const minDateString = useMemo(() => {
    // Wrap in new Date as fromDate is actually a string (should be fixed in nswag someday)
    // In this case it also instantiates a new Date that we can mutate
    const minDate = new Date(contract.phases[0].fromDate);
    // Add one day to the date
    minDate.setDate(minDate.getDate() + 1);
    // Return the date as an ISO date string without time (required to be used as mindate in datepicker)
    return toIsoDateString(minDate);
  }, [contract]);

  const readableRegistrationDate = formatDateToNOString(
    contract.phases[0].fromDate,
    true,
    '{date}. {month} {year}',
    true,
  );

  return (
    <form onSubmit={onSave}>
      <Heading level={2}>Avtaleinformasjon</Heading>
      <p>Ny avtale vil opprettes og påkrevde felter må fylles ut</p>

      {company ? (
        <ValueField label="Bedrift" value={company.name} />
      ) : (
        <ConnectCompany
          phase={selectedPhase}
          orgNumber={orgNumber}
          setOrgNumber={setOrgNumber}
          selectedCompanyName={companyName}
          setSelectedCompanyName={setCompanyName}
          setCompanyError={setCompanyError}
          showNoCompanyCheckbox={selectedPhase === SivaPhase.PreIncubation}
        />
      )}

      {(orgNumber.length > 1 || company) && (
        <div>
          <p>
            {delivered === 'yes'
              ? `Rediger egenerklæring for ${company?.name || contract.name}`
              : `Har ${company?.name || contract.name} levert egenerklæringsskjema?`}
          </p>

          <RadioButton
            onChange={(e) => setDelivered(e.currentTarget.value as 'yes' | 'no')}
            name="delivered"
            options={[
              { text: 'Ja', value: 'yes', selected: delivered === 'yes' },
              { text: 'Nei', value: 'no', selected: delivered === 'no' },
            ]}
          />
          {delivered === 'yes' && (
            <Checkbox
              label="Egenerklæring"
              name="declaration"
              options={options}
              onChange={onChangeDeclaration}
              className="margin-bottom--6"
            />
          )}
        </div>
      )}

      <SelectWrapper
        {...register('externalIndustry', {
          required: requiredI18n(tError),
        })}
        label="Velg bransje"
        options={bransjeOptions}
      />

      <DateInput
        label="Opptaksdato"
        name="startDate"
        required
        min={minDateString}
        onChange={(event) => {
          const { value } = event.target;
          setSelectedAdmissionDate(value ? new Date(value) : null);
        }}
      />
      <Alert data-color="info">
        {tSiva('error.acceptedDateAfterRegisteredDate', {
          readableRegistrationDate: readableRegistrationDate,
        })}
      </Alert>
      <br />

      <ValueField label="Utsendelse av oppstartsskjema" value={renderFormSendingDate(startupDate)} />
      <ValueField label="Utsendelse av underveisskjema" value={renderFormSendingDate(midDate)} />

      <Heading level={2}>Oppfølging</Heading>

      <Controller
        control={control}
        name="advisorId"
        render={({ field }) => (
          <Autocomplete<SelectOptionProps>
            label={tCommon('advisor')}
            name={field.name}
            items={advisorOptions}
            filter={(item, value: string): boolean => {
              const lowerCaseValue = value.toLowerCase();
              return item.text.toLowerCase().includes(lowerCaseValue) || false;
            }}
            onItemSelected={(advisor) => field.onChange(advisor.value)}
            itemLabel={(advisor) => `${advisor.text}`}
            renderItem={(advisor) => `${advisor.text}`}
            defaultValue={advisorOptions.find((x) => x.value === field.value)?.text}
          />
        )}
      />

      <p className="margin-top--0 margin-bottom--4">
        {tSiva('newCompany.contract.form.advisorId.link.text')}{' '}
        <Link to={`/actor-profile/settings/access`} target="_blank">
          <span className="margin-right--1">{tCommon('accessControl')}</span>
          <Icon prefix="fasr" iconName="external-link" />
        </Link>
      </p>

      <TextArea label="Notat" value={contract.notes} name="notes" />

      {selectedSelfDeclaration.size < 1 && (orgNumber.length > 1 || !!company) && (
        <Alert data-color="info" className="margin-top--2">
          {tSiva('reporting.selfdeclaration.Required')}
        </Alert>
      )}
      <div className="display--flex gap--1 justify-content--flex-start margin-top--7">
        <Button
          type="submit"
          disabled={companyError || (selectedSelfDeclaration.size < 1 && (orgNumber.length > 1 || !!company))}
        >
          {tCommon('save')}
        </Button>
        <Button variant="secondary" type="button" onClick={() => onCancel()}>
          {tCommon('cancel')}
        </Button>

        <DialogContainer
          show={isActive}
          onClose={updateErrorType ? deactivate : onCancel}
          title={
            updateErrorType
              ? updateErrorType === 'selfDeclaration'
                ? tSiva('changePhase.selfDeclaration.error.title')
                : tSiva('changePhase.error.title')
              : tCommon('confirmation')
          }
        >
          {updateErrorType ? (
            <>
              <p>
                {updateErrorType === 'selfDeclaration'
                  ? tSiva('changePhase.selfDeclaration.error.text')
                  : tSiva('changePhase.error.text')}
                .
              </p>
              <Button type="button" onClick={deactivate}>
                {tCommon('close')}
              </Button>
            </>
          ) : (
            <>
              <p>{`${tSiva('changePhase.success.text')} ${tSiva(`phase.${currentPhase?.sivaPhase}` as any)},
              ${tCommon('to')} ${tSiva(`phase.${selectedPhase}` as any)}`}</p>
              <Button type="button" onClick={onCancel}>
                {tCommon('close')}
              </Button>
            </>
          )}
        </DialogContainer>
      </div>
    </form>
  );
};
export default IdeaChangePhaseForm;
